import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useMarketplace } from './useMarketplace';

export interface Permissions {
    bookings: boolean;
    calendar: boolean;
    staff: boolean;
    settings: boolean;
    help: boolean;
    insights: boolean;
    twoWaySms: boolean;
    editWorkSchedule: boolean;
    blockTimeOnCalendar: boolean;
    marketing: boolean;
    services: boolean;
    products: boolean;

    clients: {
        manage: boolean;
        export: boolean;
        agreements: boolean;
        blockOrDelete: boolean;
        viewSensitiveContact: boolean;
    };
    checkout: {
        manage: boolean;
        takePayments: boolean;
        editPrices: boolean;
        refunds: boolean;
        applyDiscounts: boolean;
        reopenClosedBookings: boolean;
    };
    memberships: {
        manage: boolean;
        sell: boolean;
        viewSold: boolean;
        adjustUsage: boolean;
        pauseResume: boolean;
        editEndDate: boolean;
        cancel: boolean;
        managePayments: boolean;
    };
}

export const usePermissions = (): Permissions => {
    const auth = useAuth();
    const marketplace = useMarketplace();

    const permissions = useMemo(() => {
        const roleKey = auth?.user?.role?.key;
        if (!roleKey) {
            return new Map<string, boolean>();
        }

        return new Map(marketplace.permissions.get(roleKey) ?? []);
    }, [auth?.user?.role?.key, marketplace.permissions]);

    return {
        bookings: permissions.get('bookings.manage') ?? false,
        calendar: permissions.get('calendar.manage') ?? false,
        settings: permissions.get('insights.manage') ?? false,
        help: permissions.get('help.manage') ?? false,
        insights: permissions.get('insights.manage') ?? false,
        staff: permissions.get('staff.manage') ?? false,
        twoWaySms: permissions.get('two_way_sms.manage') ?? false,
        editWorkSchedule: permissions.get('edit_work_schedule.manage') ?? false,
        blockTimeOnCalendar: permissions.get('block_time.manage') ?? false,
        marketing: permissions.get('marketing.manage') ?? false,
        services: permissions.get('services.manage') ?? false,
        products: permissions.get('products.manage') ?? false,
        clients: {
            manage: permissions.get('clients.manage') ?? false,
            export: permissions.get('clients.export') ?? false,
            agreements: permissions.get('clients.agreements') ?? false,
            blockOrDelete: permissions.get('clients.block_or_delete') ?? false,
            viewSensitiveContact: permissions.get('clients.view_sensitive_contact') ?? false
        },
        checkout: {
            manage: permissions.get('checkout.manage') ?? false,
            takePayments: permissions.get('checkout.take_payments') ?? false,
            editPrices: permissions.get('checkout.edit_prices') ?? false,
            refunds: permissions.get('checkout.refunds') ?? false,
            applyDiscounts: permissions.get('checkout.apply_discounts') ?? false,
            reopenClosedBookings: permissions.get('checkout.reopen_closed_bookings') ?? false
        },
        memberships: {
            manage: permissions.get('memberships.manage') ?? false,
            sell: permissions.get('memberships.sell') ?? false,
            viewSold: permissions.get('memberships.view_sold') ?? false,
            adjustUsage: permissions.get('memberships.adjust_usage') ?? false,
            pauseResume: permissions.get('memberships.pause_resume') ?? false,
            editEndDate: permissions.get('memberships.edit_end_date') ?? false,
            cancel: permissions.get('memberships.cancel') ?? false,
            managePayments: permissions.get('memberships.manage_payments') ?? false
        }
    };
};

export default usePermissions;
